import { useState, useCallback } from "react";

// types
import type { IEMISelectionDetails } from "@payment-client/contexts/EmiSelectionContext";
import type { FetchBillDetailsResponse } from "@payment-client/interfaces/Checkout";

// apis
import { fetchBillDetails } from "@payment-client/apis/publicOrder";

// hooks
import { useEffectOnce } from "@swiggy-private/core-ui";

interface UseFetchBillDetailsParams {
    apiHost: string;
    selectedEmiDetails: IEMISelectionDetails | null;
}

interface UseFetchBillDetailsReturn {
    data: FetchBillDetailsResponse | null;
    loading: boolean;
    error: boolean;
}

export const useFetchBillDetails = ({
    apiHost,
    selectedEmiDetails,
}: UseFetchBillDetailsParams): UseFetchBillDetailsReturn => {
    const [data, setData] = useState<FetchBillDetailsResponse | null>(null);
    const [loading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    const fetchData = useCallback(async () => {
        if (!selectedEmiDetails) {
            setData(null);
            setError(true);
            return;
        }

        setIsLoading(true);
        setError(false);

        try {
            const { bankCode, tenure: emiTenureInMonths, emiType, cardReference } = selectedEmiDetails;
            const res = await fetchBillDetails(apiHost, {
                bankCode,
                emiTenureInMonths,
                emiType,
                cardReference: cardReference || "",
            });

            setData(res.data);
        } catch (err) {
            setError(true);
        } finally {
            setIsLoading(false);
        }
    }, [selectedEmiDetails]);

    useEffectOnce(() => {
        fetchData();
    });

    return { data, loading, error };
};
